import React, { memo } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import classNames from 'classnames';

// Minimal shape of values provided by react-custom-scrollbars onUpdate callback
type PositionValues = {
	scrollTop: number;
	scrollHeight: number;
	clientHeight: number;
};

export interface ScrollbarProps {
	autoHeight?: boolean;
	autoHeightMax?: number | string;
	autoHide?: boolean;
	thumbHeight?: number; // maps to Scrollbars thumbSize
	thumbWidth?: number;
	thumbRadius?: number;
	thumbColor?: string;
	getNewDataPortion?: () => void;
	isMobileView?: boolean;
	positionFromStartFetchNewDataPortion?: number;
	viewClassName?: string;
	className?: string;
	children?: React.ReactNode;
	// Allow passing through any additional props accepted by Scrollbars
	[key: string]: any;
}

const Scrollbar = ({
	autoHeight = true,
	autoHeightMax = '100vh',
	autoHide = false,
	thumbHeight = 200,
	thumbWidth = 8,
	thumbRadius = 8,
	thumbColor = '#E5E5E5',
	getNewDataPortion,
	isMobileView,
	positionFromStartFetchNewDataPortion = 10,
	viewClassName,
	...rest
}: ScrollbarProps) => {
	const handleScroll = (values: PositionValues) => {
		const { scrollTop, scrollHeight, clientHeight } = values;
		// position will be greater than 1 if we are about to reach the bottom
		const position =
			(scrollTop + positionFromStartFetchNewDataPortion) / (scrollHeight - clientHeight);
		if (position > 1 && typeof getNewDataPortion === 'function') getNewDataPortion();
	};

	const renderThumbVertical = ({ style, ...props }: { style?: React.CSSProperties } & any) => {
		const thumbStyle: React.CSSProperties = {
			backgroundColor: thumbColor as string,
			width: thumbWidth as number,
			zIndex: 10 as any,
			borderRadius: thumbRadius as number,
		};

		return <div className="track" style={{ ...(style || {}), ...thumbStyle }} {...props} />;
	};

	const renderView = ({ style, ...props }: { style?: React.CSSProperties } & any) => (
		<div
			className={classNames('box', viewClassName)}
			style={{
				...(style || {}),
				overflowX: 'hidden',
				paddingRight: isMobileView ? 0 : '15px',
				marginBottom: 0,
			}}
			{...props}
		/>
	);

	return (
		<Scrollbars
			onUpdate={getNewDataPortion ? handleScroll : undefined}
			className={classNames('custom-scrollbars', { 'custom-scrollbars-hide': autoHide })}
			renderView={renderView}
			renderThumbVertical={renderThumbVertical}
			renderThumbHorizontal={(props: any) => <div {...props} className="thumb-horizontal" />}
			autoHeight={autoHeight}
			autoHeightMax={autoHeightMax}
			thumbSize={thumbHeight}
			{...rest}
		/>
	);
};

export default memo(Scrollbar);
